import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormField,
  InputTextField,
  LoadingButton,
  Typography,
} from '@tempmee/react';
import { useCreateCycle, useUpdateCycle } from '../../../../domain/Awards';
import { DateField } from '../../../commonComponents/DateField';
import { cycleSubmitError } from './awardsErrors';

const emptyForm = {
  name: '',
  applicationOpenDate: '',
  applicationCloseDate: '',
  cycleEndDate: '',
};

const toForm = (cycle) =>
  cycle
    ? {
      name: cycle.name,
      applicationOpenDate: cycle.applicationOpenDate,
      applicationCloseDate: cycle.applicationCloseDate,
      cycleEndDate: cycle.cycleEndDate,
    }
    : emptyForm;

/**
 * Dates are compared as `YYYY-MM-DD` strings. That is a correct chronological
 * comparison for a zero-padded ISO date and avoids constructing a Date, which
 * would shift the day in a negative-offset timezone.
 */
const validate = ({
  name, applicationOpenDate, applicationCloseDate, cycleEndDate,
}) => {
  const errors = {};

  if (!name.trim()) errors.name = 'Cycle name is required.';
  else if (name.length > 255) errors.name = 'Cycle name must be 255 characters or fewer.';

  if (!applicationOpenDate) errors.applicationOpenDate = 'Application open date is required.';
  if (!applicationCloseDate) errors.applicationCloseDate = 'Application close date is required.';
  if (!cycleEndDate) errors.cycleEndDate = 'End date is required.';

  if (applicationOpenDate && applicationCloseDate
      && applicationCloseDate <= applicationOpenDate) {
    errors.applicationCloseDate = 'Application close must be after application open.';
  }
  if (applicationCloseDate && cycleEndDate && cycleEndDate < applicationCloseDate) {
    errors.cycleEndDate = 'End date must be on or after the application close.';
  }

  return errors;
};

/**
 * `InputTextField`'s entire prop surface (see
 * node_modules/@tempmee/react/dist/input-text-field/InputTextField.d.ts) is
 * `InputHTMLAttributes<HTMLInputElement>` plus `isError`/`leftIcon`/
 * `rightIcon`/`disabled` — there is no `label` or `helperText` prop, and the
 * error prop is `isError`, not `error`. It is composed the same way
 * `DateField` (Task 7) composes its native input: wrapped in `FormField`
 * (which renders a real `<label htmlFor>`), with the helper/error text
 * rendered as a sibling `Typography` wired up via `aria-describedby`.
 *
 * `DialogTitle`'s default dismiss button has `aria-label="Close"`, which
 * collides with the "Close" (application-close-date) field label under
 * `getByLabelText` — both become accessible names matching /Close/. The
 * dismiss button's label is overridden via `closeLabel` so the two stay
 * distinguishable; it is still wired to the dialog's `onClose`.
 */
export const CycleFormDialog = ({ open, onClose, cycle }) => {
  const isEdit = Boolean(cycle);
  const [form, setForm] = useState(() => toForm(cycle));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(toForm(cycle));
      setErrors({});
      setSubmitError(null);
    }
  }, [open, cycle]);

  const handleError = (error) => setSubmitError(cycleSubmitError(error));
  const handleSuccess = () => onClose();

  const createCycle = useCreateCycle({ onSuccess: handleSuccess, onError: handleError });
  const updateCycle = useUpdateCycle({ onSuccess: handleSuccess, onError: handleError });

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    setSubmitError(null);

    if (Object.keys(nextErrors).length > 0) return;

    if (isEdit) updateCycle.mutate({ cycleId: cycle.id, payload: form });
    else createCycle.mutate(form);
  };

  const isSubmitting = createCycle.isPending || updateCycle.isPending;
  const nameDescribedBy = errors.name ? 'cycle-name-helper' : undefined;

  // Every 409 the service raises is a date-window conflict (retroactive shrink,
  // overlap, or dates that leave the cycle out of the editable phase), so its
  // message rides with the date inputs rather than as a detached line at the
  // foot of the dialog. A 403 or a network failure is not about a field, and
  // stays dialog-level.
  const dateError = submitError?.field === 'dates' ? submitError.message : null;
  const generalError = submitError && submitError.field === null ? submitError.message : null;

  return (
    <Dialog open={open} onClose={onClose} closeOnOverlayClick={false}>
      <DialogTitle closeLabel="Dismiss dialog">
        {isEdit ? 'Edit Cycle Details' : 'Create New "Best in Dental" Cycle'}
      </DialogTitle>

      {/* DialogContent stacks its children with no gap of its own, so the
          fields ran together — the "Applications Dates" caption sat flush
          against the name field's underline. */}
      <DialogContent>
        <Column gap={4}>
          <FormField label="Cycle Name" htmlFor="cycle-name" required>
            <InputTextField
              id="cycle-name"
              value={form.name}
              onChange={(event) => setField('name')(event.target.value)}
              isError={Boolean(errors.name)}
              aria-describedby={nameDescribedBy}
            />
            {errors.name ? (
              <Typography id={nameDescribedBy} variant="caption" className="text-error">
                {errors.name}
              </Typography>
            ) : null}
          </FormField>

          <Column gap={3}>
            {/* A section caption for the three dates below, styled like one
                rather than like body copy sitting loose in the form. */}
            <Typography variant="caption" uppercase className="text-text-secondary">
              Application dates
            </Typography>

            <DateField
              id="cycle-open"
              label="Open"
              required
              value={form.applicationOpenDate}
              onChange={setField('applicationOpenDate')}
              error={Boolean(errors.applicationOpenDate)}
              helperText={errors.applicationOpenDate}
            />

            <DateField
              id="cycle-close"
              label="Close"
              required
              value={form.applicationCloseDate}
              onChange={setField('applicationCloseDate')}
              error={Boolean(errors.applicationCloseDate)}
              helperText={errors.applicationCloseDate}
            />

            <DateField
              id="cycle-end"
              label="End Date"
              required
              value={form.cycleEndDate}
              onChange={setField('cycleEndDate')}
              error={Boolean(errors.cycleEndDate)}
              helperText={errors.cycleEndDate}
            />

            {/* A rejected window (shrunk, overlapping, or out of phase) is the
                service telling the admin these dates cannot stand — so it reads
                as an error on the date group, not a stray line under the form.
                role="alert" announces it when the failed submit returns. */}
            {dateError ? (
              <Typography
                id="cycle-dates-error"
                role="alert"
                variant="caption"
                className="text-error"
              >
                {dateError}
              </Typography>
            ) : null}
          </Column>

          {generalError ? (
            <Typography variant="body2" className="text-error">
              {generalError}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>

      <DialogActions>
        <LoadingButton loading={isSubmitting} onClick={handleSubmit}>
          {isEdit ? 'Save' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

CycleFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Omit for create mode. */
  cycle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    applicationOpenDate: PropTypes.string.isRequired,
    applicationCloseDate: PropTypes.string.isRequired,
    cycleEndDate: PropTypes.string.isRequired,
  }),
};

CycleFormDialog.defaultProps = { cycle: undefined };
